import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ChevronLeft } from 'lucide-react';
import { legalSlugSchema, type LegalSlug } from '@tg-hookah/shared';
import { Spinner } from '@/components/Spinner';
import { useLegalDocument } from '@/features/legal/useLegal';

export function LegalPage() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const parsed = legalSlugSchema.safeParse(rawSlug);
  if (!parsed.success) {
    return (
      <div className="flex flex-col gap-4 px-4 pt-4 pb-8">
        <BackBar onBack={() => navigate(-1)} />
        <div className="text-hint">Документ не найден.</div>
      </div>
    );
  }
  const slug: LegalSlug = parsed.data;
  return <LegalContent slug={slug} onBack={() => navigate(-1)} />;
}

function LegalContent({ slug, onBack }: { slug: LegalSlug; onBack: () => void }) {
  const { data, isLoading, isError } = useLegalDocument(slug);

  return (
    <div className="flex flex-col gap-5 px-4 pt-3 pb-12">
      <BackBar onBack={onBack} />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : isError || !data ? (
        <div className="text-sm" style={{ color: 'var(--ember)' }}>
          Не удалось загрузить документ. Попробуйте позже.
        </div>
      ) : (
        <article className="legal-prose flex flex-col gap-3">
          <header className="flex flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
              Версия {data.version}
            </span>
            <h1 className="headline text-[20px] uppercase tracking-[0.02em]">
              {data.title}
            </h1>
            <span className="hairline w-12" />
          </header>
          <ReactMarkdown
            components={{
              h1: ({ node: _n, ...props }) => (
                <h2
                  className="font-display text-[17px] uppercase tracking-[0.02em] text-white mt-3"
                  {...props}
                />
              ),
              h2: ({ node: _n, ...props }) => (
                <h3
                  className="font-display text-[15px] uppercase tracking-[0.02em] text-white/95 mt-3"
                  {...props}
                />
              ),
              h3: ({ node: _n, ...props }) => (
                <h4
                  className="font-display text-[13px] uppercase tracking-[0.04em] text-white/90 mt-2"
                  {...props}
                />
              ),
              p: ({ node: _n, ...props }) => (
                <p className="text-[14px] leading-[1.6] text-white/85" {...props} />
              ),
              strong: ({ node: _n, ...props }) => (
                <strong className="text-white" {...props} />
              ),
              em: ({ node: _n, ...props }) => (
                <em className="text-white/65 not-italic font-mono text-[12px]" {...props} />
              ),
              ul: ({ node: _n, ...props }) => (
                <ul className="list-disc pl-5 flex flex-col gap-1.5" {...props} />
              ),
              ol: ({ node: _n, ...props }) => (
                <ol className="list-decimal pl-5 flex flex-col gap-1.5" {...props} />
              ),
              li: ({ node: _n, ...props }) => (
                <li className="text-[14px] leading-[1.55] text-white/85" {...props} />
              ),
              a: ({ node: _n, ...props }) => (
                <a
                  {...props}
                  className="underline decoration-white/40 hover:decoration-white"
                  style={{ color: '#6ea987' }}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          >
            {data.content}
          </ReactMarkdown>
        </article>
      )}
    </div>
  );
}

function BackBar({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="self-start inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/70 hover:text-white"
      style={{
        border: '1px solid var(--line-2)',
        background: 'rgba(255,255,255,0.03)',
      }}
    >
      <ChevronLeft size={14} strokeWidth={1.6} />
      Назад
    </button>
  );
}
